import { Link } from 'react-router';

export function CookiePolicyPage() {
  return (
    <div className="min-h-screen bg-base-100">
      {/* Nav */}
      <nav className="flex items-center justify-between px-6 lg:px-12 py-4 border-b border-base-300/30">
        <Link to="/">
          <div className="text-2xl font-extrabold tracking-tight">
            Hy<span className="text-primary">Pe</span>Ma
          </div>
        </Link>
        <Link to="/login" className="btn btn-ghost btn-sm text-base-content/60">
          Login
        </Link>
      </nav>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 lg:px-12 py-16">
        <h1 className="text-3xl font-bold tracking-tight mb-2">Cookie &amp; Storage Policy</h1>
        <p className="text-sm text-base-content/40 mb-10">Last updated: April 2026</p>

        <div className="space-y-10 text-base-content/70 text-sm leading-relaxed">

          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">1. What Are Cookies and Similar Technologies</h2>
            <p className="mb-4">
              Cookies are small text files placed on your device by websites you visit. They are widely
              used to make websites work efficiently and to provide information to site owners. In addition
              to cookies, modern web applications use other browser storage mechanisms such
              as <code className="text-xs bg-base-300/50 px-1 rounded">localStorage</code>,{' '}
              <code className="text-xs bg-base-300/50 px-1 rounded">sessionStorage</code>, and browser
              extension storage APIs.
            </p>
            <p>
              This policy explains which storage technologies HyPeMa (&quot;we&quot;, &quot;our&quot;,
              &quot;us&quot;), operated by OXO UG (haftungsbeschr&auml;nkt), uses across
              the web application at{' '}
              <a href="https://hypema.app" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                hypema.app
              </a>{' '}
              and the companion Chrome extension, why we use them, and how you can manage them.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">2. How We Use Storage Technologies</h2>
            <p className="mb-4">
              We categorize our use of cookies and browser storage into three groups:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Strictly Necessary</strong> — required for authentication and core functionality.
                These cannot be disabled without breaking the application. No consent is required under
                applicable data protection laws, but we disclose them here for transparency.
              </li>
              <li>
                <strong>Functional</strong> — used to remember your preferences and UI state. These
                improve your experience but are not essential for the application to function. No consent
                is required as they do not track you across sites.
              </li>
              <li>
                <strong>Analytics</strong> — used to understand how visitors interact with our website.
                These are set only after you give consent via our cookie banner.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">3. Categories of Storage</h2>

            <h3 className="font-medium text-base-content mb-3 mt-6">Strictly Necessary</h3>
            <div className="overflow-x-auto">
              <table className="table table-sm w-full">
                <thead>
                  <tr className="text-base-content/50">
                    <th>Name</th>
                    <th>Type</th>
                    <th>Purpose</th>
                    <th>Duration</th>
                    <th>Party</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code className="text-xs bg-base-300/50 px-1 rounded">sb-yhytcuiynjufrrbzfalv-auth-token</code></td>
                    <td>localStorage</td>
                    <td>Supabase authentication JWT token used to maintain your signed-in session</td>
                    <td>Session</td>
                    <td>First-party</td>
                  </tr>
                  <tr>
                    <td><code className="text-xs bg-base-300/50 px-1 rounded">auth_token</code></td>
                    <td>localStorage</td>
                    <td>Backend authentication token used for API requests</td>
                    <td>Session</td>
                    <td>First-party</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="font-medium text-base-content mb-3 mt-6">Functional</h3>
            <div className="overflow-x-auto">
              <table className="table table-sm w-full">
                <thead>
                  <tr className="text-base-content/50">
                    <th>Name</th>
                    <th>Type</th>
                    <th>Purpose</th>
                    <th>Duration</th>
                    <th>Party</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code className="text-xs bg-base-300/50 px-1 rounded">theme</code></td>
                    <td>localStorage</td>
                    <td>Stores your light/dark mode preference</td>
                    <td>Persistent</td>
                    <td>First-party</td>
                  </tr>
                  <tr>
                    <td><code className="text-xs bg-base-300/50 px-1 rounded">sidebar_collapsed</code></td>
                    <td>localStorage</td>
                    <td>Remembers whether the sidebar is collapsed or expanded</td>
                    <td>Persistent</td>
                    <td>First-party</td>
                  </tr>
                  <tr>
                    <td><code className="text-xs bg-base-300/50 px-1 rounded">selectedGroupId</code></td>
                    <td>sessionStorage</td>
                    <td>Tracks the currently selected lead group within your session</td>
                    <td>Session</td>
                    <td>First-party</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="font-medium text-base-content mb-3 mt-6">Analytics (Consent Required)</h3>
            <div className="overflow-x-auto">
              <table className="table table-sm w-full">
                <thead>
                  <tr className="text-base-content/50">
                    <th>Name</th>
                    <th>Type</th>
                    <th>Purpose</th>
                    <th>Duration</th>
                    <th>Party</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code className="text-xs bg-base-300/50 px-1 rounded">_ga</code></td>
                    <td>Cookie</td>
                    <td>Google Analytics &mdash; distinguishes unique visitors</td>
                    <td>2 years</td>
                    <td>Third-party (Google)</td>
                  </tr>
                  <tr>
                    <td><code className="text-xs bg-base-300/50 px-1 rounded">_ga_*</code></td>
                    <td>Cookie</td>
                    <td>Google Analytics &mdash; maintains session state</td>
                    <td>24 hours</td>
                    <td>Third-party (Google)</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="font-medium text-base-content mb-3 mt-6">Chrome Extension</h3>
            <p className="mb-3">
              The HyPeMa Chrome extension (&quot;HyPeMa &mdash; Gmail Draft Creator&quot;) operates in a
              separate browser context and does not set cookies. It uses a single storage entry:
            </p>
            <div className="overflow-x-auto">
              <table className="table table-sm w-full">
                <thead>
                  <tr className="text-base-content/50">
                    <th>Name</th>
                    <th>Type</th>
                    <th>Purpose</th>
                    <th>Duration</th>
                    <th>Party</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code className="text-xs bg-base-300/50 px-1 rounded">batchProgress</code></td>
                    <td>chrome.storage.local</td>
                    <td>Stores batch draft progress counters only &mdash; contains no personally identifiable information</td>
                    <td>Cleared after batch completion</td>
                    <td>First-party</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">4. How to Manage and Withdraw Consent</h2>

            <h3 className="font-medium text-base-content mb-2">Analytics cookies</h3>
            <p className="mb-4">
              Analytics cookies are only set after you provide consent via our cookie banner. You can
              withdraw your consent at any time by clicking the cookie settings link in the website
              footer, which will re-display the consent banner.
            </p>

            <h3 className="font-medium text-base-content mb-2">Browser settings</h3>
            <p className="mb-4">
              You can also manage cookies and storage through your browser settings. Most browsers
              allow you to block or delete cookies, clear localStorage and sessionStorage, and manage
              extension storage. Please note that blocking strictly necessary storage items will prevent
              you from signing in to HyPeMa.
            </p>

            <h3 className="font-medium text-base-content mb-2">Common browser links</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <a href="https://support.google.com/chrome/answer/95647" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                  Google Chrome
                </a>
              </li>
              <li>
                <a href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                  Mozilla Firefox
                </a>
              </li>
              <li>
                <a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                  Safari
                </a>
              </li>
              <li>
                <a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                  Microsoft Edge
                </a>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">5. Changes to This Policy</h2>
            <p>
              We may update this Cookie &amp; Storage Policy from time to time to reflect changes in
              the technologies we use or for regulatory reasons. When we make material changes, we will
              update the &quot;Last updated&quot; date at the top of this page. We encourage you to
              review this policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-base-content mb-3">6. Contact</h2>
            <p>
              If you have questions about our use of cookies and storage technologies, contact us at:{' '}
              <a href="mailto:service@newstelligent.com" className="text-primary hover:underline">
                service@newstelligent.com
              </a>
            </p>
            <p className="mt-2">
              OXO UG (haftungsbeschr&auml;nkt)<br />
              <a href="https://hypema.app" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                hypema.app
              </a>
            </p>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-base-300/30 py-8 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-xs text-base-content/30">
          <span>Hy<span className="text-primary/60">Pe</span>Ma</span>
          <span>Precision over volume.</span>
        </div>
      </footer>
    </div>
  );
}
